import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProtectedRoute({ children }) {
  const [authState, setAuthState] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip if OAuth callback — AuthCallback handles it
    if (window.location.hash?.includes("session_id=")) {
      setAuthState(true);
      return;
    }
    // If user was just passed via state (from login redirect)
    if (location.state?.user) {
      setAuthState(true);
      return;
    }
    const token = localStorage.getItem("lf_session");
    // No token at all → redirect immediately, no network round-trip.
    if (!token) {
      setAuthState(false);
      navigate("/admin/login", { replace: true });
      return;
    }
    axios
      .get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => setAuthState(true))
      .catch((e) => {
        // Only treat 401/403 as "definitely logged out". Transient errors
        // (5xx, network) keep the user on the protected page with a retry
        // loop in the child components, rather than ping-ponging them to
        // /admin/login — that was a major contributor to the production
        // Google-SSO infinite reload.
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("lf_session");
          setAuthState(false);
          navigate("/admin/login", { replace: true });
        } else {
          // Non-auth failure — optimistically show the page; individual
          // requests inside will fail visibly instead of ejecting the user.
          // eslint-disable-next-line no-console
          console.warn("ProtectedRoute: /auth/me non-auth failure, optimistically rendering:", e);
          setAuthState(true);
        }
      });
  }, []);

  if (authState === null) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <Loader2 size={32} className="animate-spin text-zinc-400" />
    </div>
  );
  if (authState === false) return null;
  return children;
}
